// Screen 3 - git-host install (slice-2-onboarding.md §6). GitHub and GitLab as equal
// first-class tiles (D12); the least-privilege list and no-PAT reassurance are ALWAYS
// visible, never behind a link (a trust surface). The provider redirect happens outside
// this UI (workflows.md step 2); on return, the provider's callback lands on the
// Nightcrew backend and redirects back here with `?connected=<host>` - detected on mount.
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { isErr } from "@nightcrew/shared";
import type { RepoHost } from "@nightcrew/shared";
import { FocusedFlowShell } from "../../components/FocusedFlowShell.js";
import { OuterStepper, SubStepper } from "../../components/Stepper.js";
import { getInstallUrl } from "../../api/connections.js";
import { useWizard } from "../../state/wizard.js";

type ScreenState =
  | { readonly kind: "idle" }
  | { readonly kind: "waiting"; readonly host: RepoHost; readonly url: string }
  | { readonly kind: "error"; readonly message: string }
  | { readonly kind: "denied"; readonly message: string }
  | { readonly kind: "success"; readonly host: RepoHost };

const PERMISSIONS: readonly string[] = [
  "Read repository metadata",
  "Read and write file contents",
  "Open and update pull requests",
  "Read and write issues",
  "Read and write checks and commit statuses",
  "Read and write Actions workflows, and trigger runs",
];

export const GitHostInstall = (): React.JSX.Element => {
  const navigate = useNavigate();
  const { draft } = useWizard();
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<ScreenState>({ kind: "idle" });

  useEffect(() => {
    const connected = searchParams.get("connected");
    const denied = searchParams.get("denied");
    if (connected === "github" || connected === "gitlab") {
      setState({ kind: "success", host: connected });
    } else if (denied !== null) {
      setState({
        kind: "error",
        message: "You will need to approve the connection for Nightcrew to work on this repo.",
      });
    }
  }, [searchParams]);

  const handleConnect = async (host: RepoHost): Promise<void> => {
    if (draft.appId === null) return;
    const result = await getInstallUrl({ host, appId: draft.appId });
    if (isErr(result)) {
      setState({
        kind: "error",
        message: `Something went wrong connecting to ${host === "github" ? "GitHub" : "GitLab"}. Try again.`,
      });
      return;
    }
    setState({ kind: "waiting", host, url: result.value.url });
    if (typeof window !== "undefined")
      window.open(result.value.url, "_blank", "noopener,noreferrer");
  };

  return (
    <FocusedFlowShell
      title="Connect your repo"
      onBack={() => {
        void navigate("/apps/new/intent");
      }}
      cta={
        state.kind === "success" ? (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              void navigate("/apps/new/cloud");
            }}
          >
            Continue
          </button>
        ) : undefined
      }
    >
      <OuterStepper active="connect" />
      <SubStepper
        states={{
          repo: state.kind === "success" ? "done" : "pending",
          intent: "done",
          cloud: "pending",
        }}
      />

      {state.kind === "error" ? (
        <p className="field-error" role="alert">
          {state.message}
        </p>
      ) : null}

      {state.kind === "waiting" ? (
        <div className="card pad" role="status" aria-live="polite">
          <p>Waiting for you to finish on {state.host === "github" ? "GitHub" : "GitLab"}...</p>
          <a className="chip mt8" href={state.url} target="_blank" rel="noopener noreferrer">
            If nothing happened, open it again
          </a>
        </div>
      ) : null}

      {state.kind === "success" ? (
        <div className="card pad">
          <p>Connected to {state.host === "github" ? "GitHub" : "GitLab"}.</p>
        </div>
      ) : (
        <div className="grid mt16" style={{ gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <button
            type="button"
            className="host-tile"
            onClick={() => {
              void handleConnect("github");
            }}
          >
            <span className="logo" aria-hidden="true">
              GH
            </span>
            Connect GitHub
          </button>
          <button
            type="button"
            className="host-tile"
            onClick={() => {
              void handleConnect("gitlab");
            }}
          >
            <span className="logo" aria-hidden="true">
              GL
            </span>
            Connect GitLab
          </button>
        </div>
      )}

      <div className="well pad mt16">
        <p className="eyebrow">What Nightcrew can do once connected</p>
        <ul style={{ margin: "var(--space-5) 0 0", paddingInlineStart: 20 }}>
          {PERMISSIONS.map((permission) => (
            <li key={permission} style={{ fontSize: 13.5 }}>
              {permission}
            </li>
          ))}
        </ul>
        <p className="muted mt12" style={{ fontSize: 12.5 }}>
          Nightcrew never asks for administration access, and can never delete your repository.
        </p>
      </div>
      <p className="muted mt12" style={{ fontSize: 12.5 }}>
        We never ask for a personal access token. Nightcrew connects through the provider&apos;s own
        official app install, with access that is scoped, short-lived, and revocable by you at any
        time.
      </p>
    </FocusedFlowShell>
  );
};
